import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, Send, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useStreamingChat } from '@/app/hooks/useStreamingChat';
import { useSaxaExpression, stripMoodTag, mapExpression } from '@/app/hooks/useSaxaExpression';
import { useSaxaMode } from '@/app/hooks/useSaxaMode';
import { useBackdropDark } from '@/app/hooks/useBackdropDark';
import { SaxaAvatar } from './SaxaAvatar';
import { ModeSwitcher } from './ModeSwitcher';

const QUICK_ACTIONS = [
  'How does it work?',
  'Show me pricing',
  'Compare to competitors',
  'Join the waitlist',
];

export function ChatWidget() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openChat = searchParams.get('openChat') === '1';
  const intro = searchParams.get('intro') ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeAutoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const { messages, input, handleInputChange, handleSubmit, isLoading } = useStreamingChat({
    initialMessage: intro,
  });

  // Saxa avatar mode + expression + backdrop-aware dark detection
  const { mode, setMode } = useSaxaMode();
  const rawExpression = useSaxaExpression(messages, isLoading);
  const expression = mapExpression(rawExpression, mode);
  const backdropDark = useBackdropDark(fabRef);

  // FAB expression: idle when resting, neutral when nudge is active
  const fabExpression = mapExpression(showNudge ? 'neutral' : 'idle', mode);

  // When URL has openChat=1&intro=..., open the chat and clear the params so refresh doesn't re-send
  useEffect(() => {
    if (openChat && intro) {
      setIsOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openChat');
        next.delete('intro');
        return next;
      }, { replace: true });
    }
  }, [openChat, intro, setSearchParams]);

  // Scroll to bottom effect
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Proactive nudge — show after 30s if chat hasn't been opened
  useEffect(() => {
    const dismissed = sessionStorage.getItem('saxa-nudge-dismissed');
    if (dismissed || isOpen) return;

    nudgeTimerRef.current = setTimeout(() => {
      setShowNudge(true);
    }, 30000);

    return () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, [isOpen]);

  // Clear nudge when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowNudge(false);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      if (nudgeAutoRef.current) clearTimeout(nudgeAutoRef.current);
    }
  }, [isOpen]);

  // Auto-dismiss nudge after 15s if user ignores it
  useEffect(() => {
    if (!showNudge) return;
    nudgeAutoRef.current = setTimeout(() => {
      setShowNudge(false);
      sessionStorage.setItem('saxa-nudge-dismissed', '1');
    }, 15000);
    return () => {
      if (nudgeAutoRef.current) clearTimeout(nudgeAutoRef.current);
    };
  }, [showNudge]);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    sessionStorage.setItem('saxa-nudge-dismissed', '1');
    if (nudgeAutoRef.current) clearTimeout(nudgeAutoRef.current);
  }, []);

  // Handle form submission
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Chat] form onSubmit', { input: (input ?? '').slice(0, 50), trimmed: (input ?? '').trim().length });
    if (!input || !input.trim()) return;
    handleSubmit(e);
  };

  // Handle quick action chip click
  const onQuickAction = useCallback(
    (text: string) => {
      const syntheticEvent = {
        preventDefault: () => {},
      } as React.FormEvent;

      // Set input and submit programmatically
      const inputEl = document.querySelector<HTMLInputElement>('[data-saxa-input]');
      if (inputEl) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(inputEl, text);
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
        // Small delay to let React process the input change
        setTimeout(() => {
          handleSubmit(syntheticEvent);
        }, 50);
      }
    },
    [handleSubmit]
  );

  const showChips = messages.length === 1 && !isLoading;

  return (
    <>
      {/* Proactive Nudge Bubble */}
      <AnimatePresence>
        {showNudge && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-24 right-6 z-50 flex items-center gap-2"
          >
            <div className="bg-white dark:bg-[#0F1F3D] rounded-2xl shadow-lg border border-gray-200 dark:border-[#1E3A8A]/30 px-4 py-3 max-w-[220px]">
              <button
                onClick={() => {
                  dismissNudge();
                  setIsOpen(true);
                }}
                className="text-sm text-gray-800 dark:text-white text-left leading-snug cursor-pointer"
              >
                Curious about xReplAI? I can help.
              </button>
              <button
                onClick={dismissNudge}
                className="absolute -top-2 -right-2 w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                aria-label="Dismiss"
              >
                <X size={10} />
              </button>
            </div>
            <div className="w-3 h-3 bg-white dark:bg-[#0F1F3D] border-b border-r border-gray-200 dark:border-[#1E3A8A]/30 rotate-45 -ml-4 mt-4" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            ref={fabRef}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 cursor-pointer"
            aria-label="Chat with Saxa"
          >
            <SaxaAvatar
              size="lg"
              pulse={showNudge}
              expression={fabExpression}
              mode={mode}
              dark={backdropDark}
              className={`transition-opacity duration-500 ${showNudge ? 'opacity-100' : 'opacity-60'}`}
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.3 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ transformOrigin: 'bottom right' }}
            className="fixed bottom-6 right-6 z-50 w-96 bg-white dark:bg-[#0A1628] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-[#1E3A8A]/30"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#60A5FA] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <SaxaAvatar size="md" expression={expression} mode={mode} dark />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#1E3A8A]" />
                </div>
                <div>
                  <h3 className="font-semibold">Saxa</h3>
                  <ModeSwitcher mode={mode} onModeChange={setMode} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="hover:bg-white/20 p-1.5 rounded transition-colors"
                  aria-label="Minimize chat"
                >
                  <Minimize2 size={18} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/20 p-1.5 rounded transition-colors"
                  aria-label="Close chat"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages */}
            {!isMinimized && (
              <>
                <div
                  className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-[#0A1628]/50"
                  style={{ height: isMinimized ? 0 : '460px' }}
                >
                  {messages.map((message, index) => {
                    const isAssistant = message.role !== 'user';
                    const prevMessage = index > 0 ? messages[index - 1] : null;
                    const showSaxaLabel = isAssistant && (!prevMessage || prevMessage.role === 'user');

                    return (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                      >
                        {isAssistant && (
                          <div className="flex-shrink-0 mr-2 mt-1">
                            <SaxaAvatar size="sm" expression={expression} mode={mode} />
                          </div>
                        )}
                        <div className="flex flex-col max-w-[75%]">
                          {showSaxaLabel && (
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 ml-1">
                              Saxa
                            </span>
                          )}
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              !isAssistant
                                ? 'bg-[#1E3A8A] text-white'
                                : 'bg-white dark:bg-[#1E3A8A]/20 text-gray-900 dark:text-white border border-gray-200 dark:border-[#1E3A8A]/30'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{isAssistant ? stripMoodTag(message.content) : message.content}</p>
                            {message.createdAt && (
                              <p
                                className={`text-xs mt-1 ${
                                  !isAssistant
                                    ? 'text-white/70'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}
                              >
                                {new Date(message.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                  {/* Quick Action Chips */}
                  {showChips && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="flex flex-wrap gap-2 pl-10"
                    >
                      {QUICK_ACTIONS.map((action, i) => (
                        <motion.button
                          key={action}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + i * 0.1 }}
                          onClick={() => onQuickAction(action)}
                          className="text-xs px-3 py-1.5 rounded-full border border-[#60A5FA]/40 text-[#1E3A8A] dark:text-[#60A5FA] hover:bg-[#60A5FA]/10 transition-colors cursor-pointer"
                        >
                          {action}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}

                  {/* Typing Indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div className="flex-shrink-0 mr-2 mt-1">
                        <SaxaAvatar size="sm" expression={mapExpression('focused', mode)} mode={mode} />
                      </div>
                      <div className="bg-white dark:bg-[#1E3A8A]/20 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-[#1E3A8A]/30 rounded-2xl px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs">Saxa is thinking</span>
                          <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.1s]" />
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form
                  onSubmit={onSubmit}
                  className="p-4 bg-white dark:bg-[#0A1628] border-t border-gray-200 dark:border-[#1E3A8A]/30"
                >
                  <div className="flex gap-2">
                    <input
                      type="text"
                      data-saxa-input
                      value={input || ''}
                      onChange={handleInputChange}
                      placeholder="Ask Saxa anything..."
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-[#1E3A8A]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#60A5FA] bg-white dark:bg-[#0A1628]/50 text-gray-900 dark:text-white"
                      disabled={isLoading}
                    />
                    <button
                      type="submit"
                      disabled={isLoading || !(input ?? '').trim()}
                      className="px-4 py-2 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      aria-label="Send message"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </form>

                {/* Footer Branding */}
                <div className="py-1 text-center bg-white dark:bg-[#0A1628] border-t border-gray-100 dark:border-[#1E3A8A]/10">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    Powered by Saxa
                  </span>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
